import { AfterContentChecked, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { GrocyContextDataApi } from '../../../../../core/api/grocy/context-data.api';
import { GrocyProductsApi } from '../../../../../core/api/grocy/products.api';
import { GrocyContextData } from '../../../../../core/entities/grocy/context-data/context-data.entity';
import { GrocyExpandedSubProduct } from '../../../../../core/entities/grocy/products/expanded-sub-product.entity';
import { GrocyQuantityUnitBasics } from '../../../../../core/entities/grocy/quantity-units/quantity-unit-basics.entity';
import { GrocySuccess } from '../../../../../core/entities/grocy/shared/success.entity';
import { GrocyCurrentStock } from '../../../../../core/entities/grocy/stock/current-stock.entity';
import { StockCategory } from '../../../../../core/enums/grocy/stock/stock-category.enum';
import { Animations } from '../../../../../shared/animations';
import { GrocyProductContextData } from '../../../product-actions/models/product-context-data.model';
import { getProductLink } from '../../../products/utils/product.utils';

@Component({
  selector: 'app-grocy-stock-table',
  templateUrl: './stock-table.component.html',
  styleUrl: './stock-table.component.scss',
  animations: [Animations.detailExpand],
})
export class StockTableComponent implements OnInit, AfterContentChecked {
  @ViewChild(MatSort) sort!: MatSort;

  @Input({ required: true }) dataSource!: MatTableDataSource<GrocyCurrentStock>;

  columns: string[] = ['product.name', 'productGroup', 'amount', 'bestBefore', 'value', 'expand'];
  contextData?: GrocyContextData;
  expandedRow?: GrocyCurrentStock;
  productContextData: Map<number, GrocyProductContextData> = new Map();

  constructor(
    private contextDataApi: GrocyContextDataApi,
    private productsApi: GrocyProductsApi
  ) {}

  ngOnInit(): void {
    this.contextDataApi.getContextData().subscribe(contextData => {
      this.contextData = contextData;
    });
  }

  ngAfterContentChecked(): void {
    this.dataSource.sort = this.sort;
  }

  get averageDueDate(): Date | null {
    const dueDates = this.dataSource.filteredData
      .filter(item => new Date(item.bestBefore).getFullYear() !== 2999)
      .map(item => new Date(item.bestBefore).getTime());

    if (dueDates.length === 0) return null;

    return new Date(dueDates.reduce((acc, item) => acc + item, 0) / dueDates.length);
  }

  get totalValue(): number {
    return this.dataSource.filteredData.reduce((acc, item) => acc + item.value, 0);
  }

  get numberOfProducts(): string {
    return `${this.dataSource.filteredData.length} products (${
      this.dataSource.filteredData.filter(item => item.amountOpened > 0).length
    } opened)`;
  }

  expandRow(event: MouseEvent, row: GrocyCurrentStock): void {
    this.expandedRow = this.expandedRow === row ? undefined : row;

    this.productContextData = new Map();

    this.expandedRow?.subStock.forEach(subStock => {
      this.productContextData.set(subStock.product.id, this.getProductContextData(subStock));
    });

    event.stopPropagation();
  }

  getProductLink({ parentProductId, product }: GrocyCurrentStock): string {
    return parentProductId !== null ? getProductLink(parentProductId) : getProductLink(product.id);
  }

  getCurrentStockBestBefore(currentStock: GrocyCurrentStock): string {
    if (new Date(currentStock.bestBefore).getFullYear() === 2999) {
      return 'Never';
    }

    return new Date(currentStock.bestBefore).toLocaleDateString();
  }

  getCurrentStockValue(currentStock: GrocyCurrentStock): string {
    return currentStock.value.toLocaleString('sv-SE', { style: 'currency', currency: 'SEK' });
  }

  getCurrentStockQuantityUnit(currentStock: GrocyCurrentStock): GrocyQuantityUnitBasics | undefined {
    return this.contextData?.quantityUnits.find(qu => qu.id === currentStock.quantityUnits.stock.id);
  }

  getCurrentStockAmount(currentStock: GrocyCurrentStock): string {
    const quantityUnit = this.getCurrentStockQuantityUnit(currentStock);

    let result = `${currentStock.amount} ${currentStock.amount === 1 ? quantityUnit?.name : quantityUnit?.namePlural}`;

    if (currentStock.amountOpened > 0) {
      result += ` (${currentStock.amountOpened} opened)`;
    }

    return result;
  }

  getCurrentStockColourClass(currentStock: GrocyCurrentStock): string {
    switch (currentStock.stockCategory) {
      case StockCategory.expired:
        return 'orange';
      case StockCategory.expiringSoon:
        return 'yellow';
      case StockCategory.lowStock:
        return 'accent';
      default:
        return '';
    }
  }

  productActionEvent(_: GrocySuccess): void {
    this.productsApi.findOneExpanded(this.expandedRow!.product.id).subscribe(product => {
      product.subProducts.forEach(subProduct => {
        this.updateRow(
          subProduct,
          this.expandedRow!.subStock.find(row => row.product.id === subProduct.id),
          this.expandedRow!
        );
      });
    });
  }

  private getProductContextData(currentStock: GrocyCurrentStock): GrocyProductContextData {
    return {
      amounts: {
        minimum: currentStock.minStockAmount,
        opened: currentStock.amountOpened,
        stock: currentStock.amount,
      },
      conversionPurchaseToStock: currentStock.conversionPurchaseToStock,
      defaultBestBeforeDays: currentStock.defaultBestBeforeDays,
      id: currentStock.product.id,
      lastPrice: currentStock.value / currentStock.amount,
      location: currentStock.location,
      name: currentStock.product.name,
      parentProductId: currentStock.parentProductId,
      quantityUnits: currentStock.quantityUnits,
      stockEntries: [],
      store: currentStock.store,
    };
  }

  private updateRow(product: GrocyExpandedSubProduct, row: GrocyCurrentStock | undefined, parent: GrocyCurrentStock) {
    if (row) {
      const amountDifference = product.stockAmount - row.amount;
      const amountOpenedDifference = product.stockAmountOpened - row.amountOpened;

      row.amount = product.stockAmount;
      row.amountOpened = product.stockAmountOpened;
      row.bestBefore = product.nextDueAt || new Date('2999-12-31');
      row.value = product.stockValue;

      parent.amount += amountDifference;
      parent.amountOpened += amountOpenedDifference;
    }
  }
}
